// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { SnackMessage, httpStatus } from "../../config/constant";
import { APIService } from "../../utils/apiService";
import {
  GetLandingPageStats,
  LandingPageStatsInterface,
} from "../../utils/types";
import { setStatus } from "../authSlice/authSlice";
import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: GetLandingPageStats = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: {
    totalCertifications: 0,
    totalActiveExams: 0,
  },
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const getStats = createAsyncThunk(
  "stat/getStats",
  async (
    payload: { contactId: string; userCertificationId: string },
    { dispatch }
  ) => {
    return new Promise<{ stats: LandingPageStatsInterface }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(
            AppConfig.serviceUrls.getCertificationStats +
              "/?contactId=" +
              payload.contactId +
              "&userCertificationId=" +
              payload.userCertificationId
          )
          .then((resp) => {
            if (resp.status == 200) {
              dispatch(setStatus("success"));
              resolve({ stats: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            dispatch(
              enqueueSnackbarMessage({
                message: SnackMessage.error.certificationStat,
                type: "error",
              })
            );
            reject(error);
          });
      }
    );
  }
);

export const landingStatSlice = createSlice({
  name: "landingStat",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStats.pending, (state, action) => {
        state.stateMessage = "Loading Contact";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Fetching Details";
      })
      .addCase(getStats.fulfilled, (state, action) => {
        state.response = action.payload.stats;
        state.state = "success";
        state.backgroundProcess = false;
      })
      .addCase(getStats.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
      });
  },
});

export default landingStatSlice.reducer;
